import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import PolicyHistoryGrid from "./Grids/PolicyHistoryGrid";
import apiClient from "config/apiClient";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import { UserContext } from "context";
import { NEW_COMMENT } from "constants/Constants";
import moment from "moment";
import { formErrorStrfromObj } from "utils/shared";

const PolicyHistoryDrawerContent = ({
  policyKey,
  closeDrawer,
  policyHistoryData,
  setPolicyHistoryData,
  editable,
  policyVersion,
}) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState("");
  const userContext = useContext(UserContext);
  const dispatch = useDispatch();

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleProcessData = async () => {
    if (!file) {
      dispatch(setDialog({ message: "No file selected", title: "Warning" }));
      return;
    }

    try {
      dispatch(setLoading(true));
      const formData = new FormData();
      formData.append("file", file);
      formData.append("policyKey", policyKey);

      const response = await apiClient.post("/policyHistory/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedRows = response.data.map((row) => ({
        ...row,
        type: NEW_COMMENT,
      }));

      const updatedRows = [...uploadedRows, ...policyHistoryData].map(
        (row, i) => ({
          ...row,
          id: i,
        })
      );
      setPolicyHistoryData(updatedRows);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (e) {
      dispatch(setDialog({ message: formErrorStrfromObj(e), title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAddComment = () => {
    const newRow = {
      id: Date.now(),
      versionText: policyVersion,
      policyChangeDate: moment().format("MM/DD/YYYY"),
      author: userContext?.name,
      description: comment,
      type: NEW_COMMENT,
    };
    const updatedRows = [newRow, ...policyHistoryData];
    setPolicyHistoryData(updatedRows);
    setComment("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100vw",
        ml: "10px",
        mr: "5px",
      }}
    >
      {/* Title */}
      <Typography variant="h4" fontWeight="bold" mb={2} mt={2}>
        Policy History
      </Typography>
      {/* Upload section */}

      {editable && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Bulk Upload from Translation
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <input
              type="file"
              onChange={handleFileChange}
              style={{ display: "block", maxWidth: "75%", overflow: "hidden" }}
              ref={fileInputRef}
            />
            <Button
              sx={{ mt: 1 }}
              variant="contained"
              onClick={handleProcessData}
            >
              Upload History
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              mt: 1,
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{ flex: 0.2 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Comments:
            </Typography>
            <TextField
              fullWidth
              sx={{ flex: 1 }}
              placeholder="Comment text to add to the history"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={!comment.trim()}
            >
              Add Comment
            </Button>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <PolicyHistoryGrid rows={policyHistoryData} />
      </Box>
      <Box
        sx={{
          mb: 2,
          minHeight: "70px",
          textAlign: "right",
        }}
      >
        <Button
          onClick={closeDrawer}
          variant="text"
          sx={{ fontWeight: "bold" }}
        >
          CLOSE
        </Button>
      </Box>
    </Box>
  );
};

export default PolicyHistoryDrawerContent;
